import Command from './Command';
import SelectCmd from './SelectCmd';
import CommandIdEnum from '../enums/CommandIdEnums';
import CommandStack from '../logic/CommandStack';
import Document from '../model/document/Document';
import IParameterizable from '../interfaces/IParameterizable';
import { UUID } from '../utils/typeDefinitions';
import UserRightScopeEnum from '../enums/UserRightScopeEnum';
import SceneObjectData from '../model/sceneObjectData/SceneObjectData';
import { useUserStateStore } from '../store/userStateStore';
import { useServicesState } from '../store/servicesStateStore';

export default class DeleteSceneObjectCmd extends Command implements IParameterizable<UUID> {

    #document: Document;
    #sceneObjectUuid: UUID | undefined;
    #currentUserId: string;

    constructor(commandStack: CommandStack, document: Document) {
        super(CommandIdEnum.DeleteSceneObject, commandStack);
        this.commandNameResourceKey = 'command.deleteSceneObject.title';
        this.#document = document;

        const userId = useUserStateStore.getState().currentUserId;
        if (userId == null) {
            this.#currentUserId = "";
            console.error("Cannot continue without a user ID");
            return;
        }
        this.#currentUserId = userId;
    }

    setParameters(uuid: UUID): void {
        this.#sceneObjectUuid = uuid;
    }

    canDelete(sceneObjectData: SceneObjectData): boolean {
        const userRight = this.#document.userRight;
        if (userRight.delete === UserRightScopeEnum.All) return true;
        else if (userRight.delete === UserRightScopeEnum.Own) {
            if (sceneObjectData.ownedBy && sceneObjectData.ownedBy === this.#currentUserId) return true;
        }
        return false;
    }

    async start(): Promise<void> {
        super.start();

        if (!this.#sceneObjectUuid) {
            console.error('DeleteSceneObjectCmd: no scene object UUID set');
            return;
        }

        const sceneObject = this.#document.getSceneObject(this.#sceneObjectUuid);
        if (!sceneObject) {
            console.error(`DeleteSceneObjectCmd: scene object not found for UUID ${this.#sceneObjectUuid}`);
            return;
        }

        if (!this.canDelete(sceneObject.data)) {
            console.error('DeleteSceneObjectCmd: user does not have permission to delete this scene object');
            return;
        }

        // Deselect before removing the object, so SelectCmd's selection indicator/handles (which
        // clone and track the scene object directly) don't keep pointing at a deleted object.
        const currentCmd = this.getCommandStack().getCurrentCmd();
        if (currentCmd instanceof SelectCmd) {
            currentCmd.clearSelection();
        }

        const result = await useServicesState.getState().dataProvider?.removeSceneObject(this.#sceneObjectUuid);
        if (!result) {
            console.error('DeleteSceneObjectCmd: failed to remove scene object from backend');
            return;
        }

        this.#document.removeFromDocument(sceneObject);
    }
}
